export type VehicleStatus =
  | "Работает"
  | "На ТО"
  | "Ремонт"
  | "Простой"
  | "Архив";
export type TireSeason = "Летняя" | "Зимняя";
export type EventType =
  | "income"
  | "expense"
  | "repair"
  | "maintenance"
  | "document"
  | "photo"
  | "mileage"
  | "note";
export type PaymentMethod =
  | "СБЕР"
  | "СБЕР Бизнес"
  | "Т-Бизнес"
  | "Т-Физ"
  | "1С"
  | "НАЛИЧНЫЕ"
  | "МСС"
  | "ВТБ(ПДС)";

export interface Investor {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  commissionPercent: number;
  commissionBase: "profit" | "revenue";
  personalExpenses: number;
  createdAt: string;
}

export interface Vehicle {
  id: string;
  make: string;
  model: string;
  plate: string;
  vin?: string;
  year?: number;
  mileage: number;
  nextMaintenanceMileage?: number;
  cabinFilterReplacementMileage?: number;
  airFilterReplacementMileage?: number;
  tireSeason?: TireSeason;
  stsSeries?: string;
  stsNumber?: string;
  mileageUpdatedWeek?: string;
  driverName?: string;
  driverPhone?: string;
  status: VehicleStatus;
  investorId?: string;
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceExpiresAt?: string;
  photoUrl?: string;
  createdAt: string;
}

export interface VoyagerEvent {
  id: string;
  vehicleId: string;
  type: EventType;
  title: string;
  amount?: number;
  mileage?: number;
  date: string;
  notes?: string;
  fileName?: string;
  filePath?: string;
  fileUrl?: string;
  documentExpiresAt?: string;
  paid?: boolean;
  expenseScope?: "vehicle" | "investor_personal";
  investorId?: string;
  paymentMethod?: PaymentMethod;
  source?: "downtime_table" | "vehicle_inspection";
  inspectionId?: string;
  createdAt: string;
}

export interface DowntimeZeroDay {
  vehicleId: string;
  date: string;
}

export type RepairRecommendationPriority = "Низкий" | "Средний" | "Высокий";
export type RepairRecommendationStatus = "Не выполнено" | "Выполнено";
export type RepairPartStatus = "Нет в наличии" | "Заказано" | "В наличии";

export interface RepairRecommendationPart {
  id: string;
  name: string;
  status: RepairPartStatus;
  storageLocation?: string;
}

export interface RepairRecommendation {
  id: string;
  vehicleId: string;
  date: string;
  recommendation: string;
  priority: RepairRecommendationPriority;
  plannedRepairDate?: string;
  status: RepairRecommendationStatus;
  actualRepairDate?: string;
  comment?: string;
  parts: RepairRecommendationPart[];
  repairEventId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvestorPayment {
  id: string;
  investorId: string;
  amount: number;
  date: string;
  periodMonth?: string;
  notes?: string;
  createdAt: string;
}

export interface VoyagerSettings {
  monthlyVehicleExpenseLimit: number;
  insuranceAlertDays: number;
}

export interface PlannedPaymentOverride {
  date?: string;
  payee?: string;
  amount?: number | null;
  comment?: string;
  cancelled?: boolean;
}

export interface PlannedPayment {
  id: string;
  payee: string;
  amount?: number;
  date: string;
  comment?: string;
  repeatMonthly: boolean;
  paidDates: string[];
  overrides?: Record<string, PlannedPaymentOverride>;
  createdAt: string;
}

export interface ReferenceItem {
  id: string;
  title: string;
  kind: "table" | "link" | "note";
  category?: string;
  url?: string;
  description?: string;
  createdAt: string;
}

export type BicycleStatus =
  | "Свободен"
  | "В аренде"
  | "На ТО"
  | "Ремонт"
  | "Архив";
export type BicycleEventType =
  | "rental"
  | "income"
  | "expense"
  | "repair"
  | "maintenance"
  | "document"
  | "mileage"
  | "note";
export type BicycleRentalPaymentMethod = "Наличные" | "МНС" | "МСС" | "ПДС";
export type BicycleRentalPlan =
  | "Не уточнено"
  | "Будет продлевать"
  | "Вернёт велосипед";
export type BicycleRepairUrgency = "Срочный" | "Не срочный";
export type BicycleRepairService =
  | "Не выбран"
  | "Наш сервис"
  | "ЭлВел"
  | "Плюс Минус"
  | "Другой";
export type BicycleRepairPayer = "Не выбран" | "Арендатор" | "Автопарк";
export type BicycleRepairStatus = "Не выполнено" | "Выполнено";
export type BicycleWaitlistModel = "Titan 2.0" | "UNO" | "Monster Pro";
export type BicycleGeneralExpenseCategory =
  | "Запчасти на склад"
  | "Расходники"
  | "Инструмент"
  | "Общий ремонт"
  | "Доставка"
  | "Прочее";

export interface BicycleGeneralExpense {
  id: string;
  date: string;
  amount: number;
  category: BicycleGeneralExpenseCategory;
  service?: BicycleRepairService;
  customService?: string;
  paymentMethod?: PaymentMethod;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Bicycle {
  id: string;
  brand: string;
  model: string;
  inventoryNumber: string;
  serialNumber?: string;
  batteryNumber?: string;
  chargerNumber?: string;
  year?: number;
  mileage: number;
  purchasePrice?: number;
  status: BicycleStatus;
  currentRenter?: string;
  rentalUntil?: string;
  rentalPlan?: BicycleRentalPlan;
  rentalPlanNote?: string;
  rentalPlanUpdatedAt?: string;
  createdAt: string;
}

export interface BicycleEvent {
  id: string;
  bicycleId: string;
  type: BicycleEventType;
  title: string;
  amount?: number;
  mileage?: number;
  date: string;
  notes?: string;
  renter?: string;
  renterPhone?: string;
  rentalUntil?: string;
  rentalPaymentMethod?: BicycleRentalPaymentMethod;
  bicycleModel?: string;
  bicycleInventoryNumber?: string;
  fileName?: string;
  filePath?: string;
  documentExpiresAt?: string;
  paymentMethod?: PaymentMethod;
  source?: "bicycle_inspection";
  inspectionId?: string;
  createdAt: string;
}

export interface BicycleRepairOrder {
  id: string;
  bicycleId: string;
  urgency: BicycleRepairUrgency;
  repairs: string[];
  service: BicycleRepairService;
  customService?: string;
  laborCost?: number;
  payer: BicycleRepairPayer;
  status: BicycleRepairStatus;
  notes?: string;
  sourceInspectionId?: string;
  needsCompletion?: boolean;
  createdAt: string;
  completedAt?: string;
}

export type BicycleInspectionStatus = "pending" | "archive" | "damaged";

export interface BicycleInspectionDamage {
  id: string;
  part: string;
  description?: string;
  amount?: number;
}

export interface BicycleInspectionMaintenanceItem {
  id: string;
  work: string;
  urgency: BicycleRepairUrgency;
}

export interface BicycleInspectionPhoto {
  id: string;
  fileName: string;
  filePath: string;
  createdAt: string;
}

export interface BicycleInspection {
  id: string;
  bicycleId: string;
  bicycleInventoryNumber: string;
  bicycleName: string;
  renterName: string;
  scheduledAt: string;
  status: BicycleInspectionStatus;
  mileage?: number;
  hasDamage?: boolean;
  damages: BicycleInspectionDamage[];
  requiresMaintenance?: boolean;
  maintenanceItems: BicycleInspectionMaintenanceItem[];
  comment?: string;
  photos: BicycleInspectionPhoto[];
  inspectorName?: string;
  linkedRepairOrderId?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BicycleWaitlistEntry {
  id: string;
  renterName: string;
  renterPhone: string;
  model: BicycleWaitlistModel;
  notes?: string;
  createdAt: string;
}

export type RentalCarStatus =
  | "Доступен"
  | "Забронирован"
  | "В аренде"
  | "Обслуживание"
  | "Архив";
export type RentalBookingStatus = "Бронь" | "Выдан" | "Завершён" | "Отменён";
export type RentalEventType =
  | "income"
  | "expense"
  | "repair"
  | "maintenance"
  | "document"
  | "damage"
  | "mileage"
  | "note";

export interface RentalRateTier {
  id: string;
  minDays: number;
  dailyRate: number;
}

export interface RentalCar {
  id: string;
  make: string;
  model: string;
  plate: string;
  vin?: string;
  year?: number;
  mileage: number;
  purchasePrice?: number;
  stsSeries?: string;
  stsNumber?: string;
  status: RentalCarStatus;
  dailyRate: number;
  defaultDeposit: number;
  rateTiers?: RentalRateTier[];
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  insuranceExpiresAt?: string;
  createdAt: string;
}

export interface RentalBooking {
  id: string;
  rentalCarId: string;
  clientName: string;
  clientPhone?: string;
  startDate: string;
  endDate: string;
  pickupTime?: string;
  returnTime?: string;
  dailyRate: number;
  deposit: number;
  status: RentalBookingStatus;
  notes?: string;
  createdAt: string;
}

export interface RentalEvent {
  id: string;
  rentalCarId: string;
  bookingId?: string;
  type: RentalEventType;
  title: string;
  amount?: number;
  mileage?: number;
  date: string;
  notes?: string;
  fileName?: string;
  filePath?: string;
  documentExpiresAt?: string;
  paymentMethod?: PaymentMethod;
  createdAt: string;
}

export type AccidentFaultStatus = "Виновен" | "Не виновен";
export type AccidentRegistrationMethod =
  | "ГАИ"
  | "Аварийный комиссар"
  | "Решили на месте";
export type AccidentAttachmentKind = "photo" | "document";

export interface AccidentAttachment {
  id: string;
  kind: AccidentAttachmentKind;
  fileName: string;
  filePath: string;
  createdAt: string;
}

export interface AccidentRecord {
  id: string;
  vehicleId: string;
  vehiclePlate: string;
  vehicleName: string;
  date: string;
  driverName: string;
  faultStatus: AccidentFaultStatus;
  registrationMethod: AccidentRegistrationMethod;
  damageAmount: number;
  paid: boolean;
  comment?: string;
  attachments: AccidentAttachment[];
  createdAt: string;
  updatedAt: string;
}

export type VehicleInspectionStatus = "pending" | "archive" | "damaged";

export interface VehicleInspectionPhoto {
  id: string;
  fileName: string;
  filePath: string;
  createdAt: string;
}

export interface VehicleInspection {
  id: string;
  vehicleId: string;
  vehiclePlate: string;
  vehicleName: string;
  scheduledAt: string;
  driverName: string;
  status: VehicleInspectionStatus;
  mileage?: number;
  mileageEventId?: string;
  hasDamage?: boolean;
  damageDescription?: string;
  damageAmount?: number;
  lateFine?: number;
  washFine?: number;
  comment?: string;
  photos: VehicleInspectionPhoto[];
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type NikitaMoneyEntryKind = "income" | "expense";
export type NikitaMoneyCategory =
  | "Короб"
  | "Аренда"
  | "Прокат"
  | "Велосипед"
  | "Авито"
  | "Пополнение 1С"
  | "Ремонты"
  | "Прочее";

export interface NikitaMoneyEntry {
  id: string;
  kind: NikitaMoneyEntryKind;
  date: string;
  amount: number;
  category: NikitaMoneyCategory;
  comment?: string;
  createdAt: string;
  updatedAt: string;
}

export type WarehouseLocation = "Офис" | "Гараж";
export type WarehouseCategory = "Автозапчасти" | "Запчасти электровелосипедов" | "Расходники" | "Материалы" | "Инструменты" | "Прочее";
export type WarehouseUnit = "шт." | "компл." | "л" | "м" | "кг";
export type WarehousePayer = "МНС" | "МСС" | "ПДС" | "ЕДВ" | "Инвестор";
export type WarehouseMovementKind = "create" | "receipt" | "issue" | "transfer" | "edit";

export interface WarehouseItem {
  id: string;
  name: string;
  article?: string;
  category: WarehouseCategory;
  unit: WarehouseUnit;
  unitPrice?: number;
  paidBy?: WarehousePayer;
  comment?: string;
  location: WarehouseLocation;
  quantities: Record<WarehouseLocation, number>;
  lastMovementId: string;
  createdAt: string;
  updatedAt: string;
}

export interface WarehouseMovement {
  id: string;
  itemId: string;
  kind: WarehouseMovementKind;
  quantity: number;
  from?: WarehouseLocation;
  to?: WarehouseLocation;
  itemName: string;
  article?: string;
  unit: WarehouseUnit;
  paidBy?: WarehousePayer;
  comment?: string;
  actorId: string;
  actorName: string;
  date: string;
}

export type BicyclePriceKind = "work" | "part";
export type BicyclePriceMode = "fixed" | "from";
export type BicyclePriceUnit = "услуга" | "шт." | "пара" | "компл.";
export interface BicyclePriceItem {
  id: string;
  kind: BicyclePriceKind;
  name: string;
  category: string;
  models: string[];
  price: number;
  priceMode: BicyclePriceMode;
  unit: BicyclePriceUnit;
  article?: string;
  comment?: string;
  archived: boolean;
  revision: string;
  createdAt: string;
  updatedAt: string;
}

export interface VoyagerData {
  schemaVersion: number;
  vehicles: Vehicle[];
  investors: Investor[];
  events: VoyagerEvent[];
  references: ReferenceItem[];
  bicycles: Bicycle[];
  bicycleEvents: BicycleEvent[];
  bicycleGeneralExpenses: BicycleGeneralExpense[];
  bicyclePriceItems: BicyclePriceItem[];
  bicycleRepairOrders: BicycleRepairOrder[];
  bicycleWaitlist: BicycleWaitlistEntry[];
  bicycleInspections: BicycleInspection[];
  rentalCars: RentalCar[];
  rentalBookings: RentalBooking[];
  rentalEvents: RentalEvent[];
  accidentRecords: AccidentRecord[];
  vehicleInspections: VehicleInspection[];
  nikitaMoneyEntries: NikitaMoneyEntry[];
  warehouseItems: WarehouseItem[];
  warehouseMovements: WarehouseMovement[];
  investorPayments: InvestorPayment[];
  plannedPayments: PlannedPayment[];
  downtimeZeroDays: DowntimeZeroDay[];
  downtimeHiddenVehicleIds: string[];
  repairRecommendations: RepairRecommendation[];
  repairStorageLocations: string[];
  settings: VoyagerSettings;
  updatedAt: string;
}

export const emptyData = (): VoyagerData => ({
  schemaVersion: 15,
  vehicles: [],
  investors: [],
  events: [],
  references: [],
  bicycles: [],
  bicycleEvents: [],
  bicycleGeneralExpenses: [],
  bicyclePriceItems: [],
  bicycleRepairOrders: [],
  bicycleWaitlist: [],
  bicycleInspections: [],
  rentalCars: [],
  rentalBookings: [],
  rentalEvents: [],
  accidentRecords: [],
  vehicleInspections: [],
  nikitaMoneyEntries: [],
  warehouseItems: [],
  warehouseMovements: [],
  investorPayments: [],
  plannedPayments: [],
  downtimeZeroDays: [],
  downtimeHiddenVehicleIds: [],
  repairRecommendations: [],
  repairStorageLocations: ["Офис", "Вальтер"],
  settings: {
    monthlyVehicleExpenseLimit: 17000,
    insuranceAlertDays: 30,
  },
  updatedAt: new Date().toISOString(),
});
